<template>
	<modal
		v-if="showDialog"
		v-model="showDialog"
		:title="$t('edit-sip-user')"
		width="400"
		type="question"
		:confirm-text="$t('save')"
		:cancel-text="$t('cancel')"
		:is-cta-disable="isCtaDisable"
		:confirm-loading="loading"
		:show-save-icon="true"
		persistent
		content-class="devops-subdialog"
		show-close-button
		@cancel="showDialog = false"
		@confirm="handleSubmit"
	>
		<v-form ref="form" v-model="validForm" class="px-4" @submit.prevent="handleSubmit">
			<v-col class="pa-0">
				<v-text-field
					v-model="displayName"
					:rules="[rules.required(displayName, 'displayName'), rules.minLength(displayName), rules.maxLength(displayName)]"
					dense
					outlined
					background-color="light-primary"
					:label="$t('display-name-required')"
				/>

				<v-text-field
					v-model="password"
					:rules="password?.length ? [rules.passwordFormat(password)] : []"
					dense
					outlined
					background-color="light-primary"
					:label="$t('sip-password')"
					:hint="$t('sip-password-hint')"
					persistent-hint
					:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
					:type="showPassword ? 'text' : 'password'"
					@click:append="showPassword = !showPassword"
				>
					<template #message="{ message }">
						<span>{{ message }}</span>
						<span v-if="password?.length" class="d-block red--text font-weight-bold mt-1">
							⚠️ {{ $t('sip-password-warning') }}
						</span>
					</template>
				</v-text-field>

				<div class="grey lighten-4 rounded pa-3 mt-4 mb-2">
					<div class="text-caption text--secondary text-center mb-2">
						{{ $t('sip-advanced-settings-hint') }}
					</div>

					<v-switch
						v-model="privateRecording"
						:label="$t('sip-private-recording')"
						class="mt-0 mb-0"
						dense
						hide-details
					/>

					<v-select
						v-model="defaultUserGroupId"
						:items="groupItems"
						:label="$t('sip-default-group')"
						:hint="$t('sip-default-group-hint')"
						persistent-hint
						dense
						outlined
						background-color="white"
						clearable
						class="mt-4"
						:no-data-text="$t('no-groups-available')"
					/>
				</div>
			</v-col>
		</v-form>
	</modal>
</template>

<script>
import Modal from "@/components/common/Modal.vue";
import { RequiredValidator } from "@/validators";

export default {
	name: "EditSipUserDialog",
	components: { Modal },

	props: {
		value: Boolean,

		user: {
			type: Object,
			default: null
		},

		groups: {
			type: Array,
			default: () => []
		}
	},

	data: () => ({
		validForm: false,
		displayName: "",
		password: "",
		privateRecording: false,
		defaultUserGroupId: null,
		showPassword: false,
		loading: false
	}),

	computed: {
		rules() {
			return {
				required: RequiredValidator,
				maxLength: value => !value || value.length <= 32 || this.$t("error.validator.maxLength", { maxLength: 32 }),
				minLength: value => !value || value.length >= 2 || this.$t("error.validator.minLength", { minLength: 2 }),
				passwordFormat: value => {
					if (!value?.length) return true;
					return /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,}$/.test(value) || this.$t("sip-password-format-error");
				}
			};
		},

		groupItems() {
			return this.groups.map(group => ({
				text: group.label || group.id,
				value: group.id
			}));
		},

		showDialog: {
			get() {
				return this.value;
			},

			set(value) {
				this.$emit("input", value);
			}
		},

		isCtaDisable() {
			return {
				cancel: this.loading,
				confirm: !this.validForm
			};
		}
	},

	watch: {
		showDialog(value) {
			if (value && this.user) {
				this.displayName = this.user.displayName || "";
				this.password = "";
				this.privateRecording = !!this.user.privateRecording;
				this.defaultUserGroupId = this.user.defaultUserGroupId || null;
				this.showPassword = false;
			} else if (!value) {
				this.resetForm();
			}
		}
	},

	methods: {
		async handleSubmit() {
			if (!this.user?.username) {
				this.$error(this.$t("error.update-sip-user"));
				return;
			}

			try {
				this.loading = true;

				const payload = {
					username: this.user.username,
					displayName: this.displayName,
					privateRecording: this.privateRecording,
					defaultUserGroupId: this.defaultUserGroupId || ""
				};

				if (this.password?.length) {
					payload.password = this.password;
				}

				await this.axios.put("/sips/v1/users", payload);
				this.$success(this.$t("edit-sip-user-success"));
				this.$emit("updated");
			} catch (error) {
				console.error(error);
				this.$error(this.$t("error.update-sip-user"));
			} finally {
				this.loading = false;
				this.showDialog = false;
			}
		},

		resetForm() {
			this.displayName = "";
			this.password = "";
			this.privateRecording = false;
			this.defaultUserGroupId = null;
			this.showPassword = false;
			if (this.$refs.form) {
				this.$refs.form.resetValidation();
			}
		}
	}
};
</script>
